import * as React from 'react';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { AppBar, Toolbar, Typography, Box, useTheme, useMediaQuery, IconButton, Grid, List } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignOut, faBars, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { NavItems } from './NavItems';
import { PlatformUtils } from '../../utils';
import { CerebroHeading, CerebroIcon, CerebroTextButton } from '..';
import { fontFamily, useCerebroNav, weights } from '../../context';
import { CerebroNavigationDrawer } from './CerebroNavigationDrawer';
import { PlatformRootUrl } from '../../utils/platform';
import { StatusBar } from './StatusBar';
import { useDealQuery } from '../../hooks/Queries';
import { Deal } from '../../models/Deal';
import { getErrorStatus } from '../../utils/axios';

export const Layout: React.FC = () => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const cerebroNav = useCerebroNav();
	const [mobileNavOpen, setMobileNavBarOpen] = React.useState(false);

	const location = useLocation();

	React.useEffect(() => {
		if (!isMobile && mobileNavOpen) setMobileNavBarOpen(false);
	}, [isMobile, mobileNavOpen]);

	React.useEffect(() => {
		// Close the mobile nav bar whenever the navigation changes
		if (mobileNavOpen) setMobileNavBarOpen(false);
	}, [location]); // eslint-disable-line react-hooks/exhaustive-deps

	const { guid } = useParams<'guid'>();

	const dealQuery = useDealQuery(guid);

	if (!guid || (dealQuery.error && getErrorStatus(dealQuery.error) == 401)) {
		window.location.href = PlatformRootUrl;
		return null;
	}

	if (dealQuery.isSuccess && dealQuery.data.isRawLead) {
		window.location.href = `${PlatformRootUrl}/Emp/RawLeadData?id=${dealQuery.data.guid}`;
		return null;
	}

	return (
		<Box sx={{ overflowX: 'hidden' }}>
			<AppBar
				position="relative"
				sx={theme => ({
					backgroundColor: theme.palette.gray.background,
					height: 64,
					justifyContent: 'center',
					zIndex: 1000,
				})}
			>
				<Grid container component={Toolbar} justifyContent={isMobile ? 'center' : 'space-between'}>
					<Grid item xs={12} md="auto">
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
							}}
						>
							{isMobile && (
								<IconButton onClick={() => setMobileNavBarOpen(!mobileNavOpen)} sx={{ marginRight: 2 }}>
									<CerebroIcon sx={theme => ({ color: theme.palette.gray.dark })}>
										<FontAwesomeIcon icon={faBars} size="lg" />
									</CerebroIcon>
								</IconButton>
							)}
							<a href={PlatformRootUrl} style={{ lineHeight: 0 }}>
								<img src={`${process.env.PUBLIC_URL}/Cerebro-Capital-Logo.jpg`} width="210"></img>
							</a>
						</Box>
					</Grid>
					{!isMobile && (
						<>
							<Grid item>
								<Typography
									variant="h3"
									component="div"
									sx={theme => ({
										color: theme.palette.gray.dark,

										fontFamily,
										fontWeight: weights.semibold,
										fontSize: 22,

										lineHeight: '27px',

										textAlign: 'center',
									})}
								>
									{dealQuery.isSuccess ? (
										<>
											LOAN APPLICATION
											<br></br>
											<CerebroHeading size="h3">{getPageHeader(dealQuery.data)}</CerebroHeading>
										</>
									) : (
										<>LOAN APPLICATION</>
									)}
								</Typography>
							</Grid>
							<Grid item>
								<CerebroTextButton href={PlatformUtils.GetLogoutUrl()}>
									Logout
									<CerebroIcon padLeft sx={theme => ({ color: theme.palette.primary.main })}>
										<FontAwesomeIcon icon={faSignOut} size="lg" />
									</CerebroIcon>
								</CerebroTextButton>
							</Grid>
						</>
					)}
				</Grid>
			</AppBar>
			<Box sx={{ display: isMobile ? 'block' : 'flex', minHeight: `calc(100vh - 64px)` }}>
				<CerebroNavigationDrawer open={mobileNavOpen} onClose={() => setMobileNavBarOpen(false)}>
					{isMobile && (
						<Grid
							container
							component={Toolbar}
							justifyContent="space-between"
							alignItems="center"
							sx={{ boxShadow: 3 }}
						>
							<Grid item>
								<IconButton onClick={() => setMobileNavBarOpen(!mobileNavOpen)} sx={{ marginRight: 2 }}>
									<CerebroIcon sx={theme => ({ color: theme.palette.gray.dark })}>
										<FontAwesomeIcon fixedWidth icon={faTimes} size="lg" />
									</CerebroIcon>
								</IconButton>
							</Grid>

							<Grid item>
								<CerebroTextButton href={PlatformUtils.GetLogoutUrl()}>
									Logout
									<CerebroIcon padLeft sx={theme => ({ color: theme.palette.primary.main })}>
										<FontAwesomeIcon icon={faSignOut} size="lg" />
									</CerebroIcon>
								</CerebroTextButton>
							</Grid>
						</Grid>
					)}
					<List dense>
						<NavItems level={1} navItems={cerebroNav.navItems} />
					</List>
				</CerebroNavigationDrawer>
				<Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
					<StatusBar />
					<Outlet />
				</Box>
			</Box>
		</Box>
	);
};

function getPageHeader(data: Deal) {
	return !!data.dealName ? `${data.companyName} - ${data.dealName}` : `${data.companyName}`;
}
